import { useState, useEffect } from "react";
import { useParams, Link, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { LogoWordmark } from "../components/Logo";
import { Footer } from "../components/Footer";
import { useLang, STR, pathForLang } from "../i18n";
import { PROJECT_DETAILS } from "../projectDetails";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export const ProjectPage = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const { pathname } = useLocation();
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const project = PROJECT_DETAILS[slug];
  if (!project) return <Navigate to={lang === "en" ? "/en/" : "/"} replace />;

  const t = project[lang];
  const homePath = lang === "en" ? "/en/" : "/";
  const contactHref = `${homePath}#contatti`;
  let imgIndex = -1;

  return (
    <div data-testid="project-page">
      <header className="bg-white border-b border-[#edf0f2]" data-testid="project-header">
        <div className="max-w-[1320px] mx-auto flex items-center justify-between px-6 h-[104px]">
          <Link to={homePath} data-testid="project-logo-link">
            <LogoWordmark compact />
          </Link>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1 text-[11px] font-bold tracking-[0.08em]" data-testid="lang-switch">
              {["it", "en"].map((l) => (
                <a
                  key={l}
                  href={pathForLang(pathname, l)}
                  hrefLang={l}
                  data-testid={`lang-${l}`}
                  className={`px-2 py-1 uppercase transition-colors duration-300 ${
                    lang === l ? "text-[#F18A00]" : "text-[#0D1B2A]/40 hover:text-[#0D1B2A]"
                  }`}
                >
                  {l}
                </a>
              ))}
            </div>
            <a
              href={`${homePath}#progetti`}
              className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.045em] text-[#0D1B2A] border border-[#0D1B2A] px-5 py-3 hover:border-[#F18A00] hover:text-[#F18A00] transition-colors duration-300"
              data-testid="project-back-link"
            >
              <ArrowLeft size={14} /> {STR[lang].legal.back}
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Hero del progetto */}
        <div className="relative h-[420px] md:h-[560px] overflow-hidden" data-testid="project-hero">
          <img src={project.hero} alt={t.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D1B2A]/55" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1320px] mx-auto px-6 pb-12 w-full">
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="text-[11px] uppercase tracking-[0.2em] text-[#F18A00] font-bold"
                data-testid="project-tagline"
              >
                {t.tagline}
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-medium text-[38px] md:text-[54px] tracking-[-0.02em] text-white mt-3"
                data-testid="project-title"
              >
                {t.name}
              </motion.h1>
            </div>
          </div>
        </div>

        {/* Intro */}
        <div className="bg-white py-[70px]">
          <div className="max-w-[860px] mx-auto px-6">
            <div data-testid="project-intro">
              {(Array.isArray(t.intro) ? t.intro : [t.intro]).map((p, i) => (
                <motion.p key={i} {...reveal} className="text-base md:text-lg leading-[1.8] text-[#4A4F55] mb-5 last:mb-0">
                  {p}
                </motion.p>
              ))}
            </div>
          </div>
        </div>

        {/* Sezioni testo + immagini */}
        {t.sections.map((sec, si) => (
          <section key={si} className={si % 2 === 0 ? "bg-[#f7f8f9] py-[70px]" : "bg-white py-[70px]"} data-testid={`project-section-${si}`}>
            <div className="max-w-[860px] mx-auto px-6">
              <motion.div {...reveal}>
                <span className="block w-[38px] h-0.5 bg-[#F18A00] mb-[18px]" aria-hidden="true" />
                <h2 className="font-display font-medium text-[26px] md:text-[32px] tracking-[-0.02em] text-[#0D1B2A] mb-[18px]">
                  {sec.title}
                </h2>
                {sec.paragraphs.map((p, pi) => (
                  <p key={pi} className="text-sm md:text-base leading-[1.8] text-[#4A4F55] mb-4">
                    {p}
                  </p>
                ))}
              </motion.div>
            </div>
            {sec.images.length > 0 && (
              <motion.div
                {...reveal}
                className={`mx-auto px-6 mt-[34px] ${
                  sec.layout === "wide" ? "max-w-[1100px]" : "max-w-[1100px] grid gap-[13px] sm:grid-cols-2 lg:grid-cols-3"
                }`}
              >
                {sec.images.map((src) => {
                  imgIndex += 1;
                  const n = imgIndex;
                  return (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setZoom(src)}
                      className="block w-full overflow-hidden cursor-zoom-in group"
                      data-testid={`project-image-${n}`}
                    >
                      <img
                        src={src}
                        alt={`${t.name} — ${sec.title}`}
                        loading="lazy"
                        className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                          sec.layout === "wide" ? "max-h-[560px]" : "h-[260px]"
                        }`}
                      />
                    </button>
                  );
                })}
              </motion.div>
            )}
          </section>
        ))}

        {/* CTA finale */}
        <section className="bg-[#0D1B2A] py-[80px]" data-testid="project-cta">
          <div className="max-w-[860px] mx-auto px-6 text-center">
            <motion.div {...reveal}>
              <h2 className="font-display font-medium text-[26px] md:text-[34px] tracking-[-0.02em] text-white mb-5">
                {t.cta.title}
              </h2>
              <p className="text-sm md:text-base leading-[1.8] text-white/70 mb-8">{t.cta.text}</p>
              <a
                href={contactHref}
                className="inline-flex items-center gap-3 bg-[#F18A00] text-white text-xs uppercase tracking-[0.045em] font-bold px-8 py-4 hover:bg-[#d67a00] transition-colors duration-300"
                data-testid="project-cta-button"
              >
                {t.cta.button} <ArrowRight size={14} />
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Zoom immagine */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0D1B2A]/95 flex items-center justify-center p-6"
            onClick={() => setZoom(null)}
            data-testid="project-lightbox"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setZoom(null)}
              className="absolute top-5 right-5 text-white/70 hover:text-[#F18A00] transition-colors"
              aria-label="Chiudi"
              data-testid="project-lightbox-close"
            >
              <X size={30} />
            </button>
            <motion.img
              key={zoom}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              src={zoom}
              alt={t.name}
              className="max-w-full max-h-[85vh] object-contain"
              data-testid="project-lightbox-image"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
